import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createTask, getTasks, completeTask, deleteTask, updateTask } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClearableInput } from '../components/ui/clearable-input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import {
    ListTodo,
    Plus,
    CheckCircle2,
    Circle,
    Trash2,
    Apple,
    Moon,
    Dumbbell,
    Plane,
    Star,
    AlertCircle,
    Clock,
    Pencil,
    Check,
    Heart,
    BatteryCharging
} from 'lucide-react';

const categories = [
    { value: 'general', label: 'Allgemein', icon: ListTodo, color: 'bg-gray-100 dark:bg-gray-800' },
    { value: 'nutrition', label: 'Ernährung', icon: Apple, color: 'bg-lime-100 dark:bg-lime-900/30' },
    { value: 'sleep', label: 'Schlaf', icon: Moon, color: 'bg-indigo-100 dark:bg-indigo-900/30' },
    { value: 'fitness', label: 'Fitness', icon: Dumbbell, color: 'bg-orange-100 dark:bg-orange-900/30' },
    { value: 'wellbeing', label: 'Wellbeing', icon: Heart, color: 'bg-pink-100 dark:bg-pink-900/30' },
    { value: 'recovery', label: 'Erholung', icon: BatteryCharging, color: 'bg-teal-100 dark:bg-teal-900/30' },
    { value: 'travel', label: 'Reise', icon: Plane, color: 'bg-sky-100 dark:bg-sky-900/30' },
];

const priorities = [
    { value: 'low', label: 'Niedrig', color: 'text-green-500' },
    { value: 'medium', label: 'Mittel', color: 'text-yellow-500' },
    { value: 'high', label: 'Hoch', color: 'text-red-500' },
];

export default function Tasks() {
    const { language } = useLanguage();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filter, setFilter] = useState('pending');
    const [taskForm, setTaskForm] = useState({
        title: '',
        description: '',
        category: 'general',
        priority: 'medium',
        due_date: '',
        recurrence: 'none',
        points: 10
    });

    useEffect(() => {
        loadTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const loadTasks = async () => {
        try {
            const completed = filter === 'completed' ? true : filter === 'pending' ? false : undefined;
            const response = await getTasks(completed);
            setTasks(response.data);
        } catch (error) {
            console.error('Failed to load tasks:', error);
            toast.error('Fehler beim Laden der Aufgaben');
        } finally {
            setLoading(false);
        }
    };

    const handleAddTask = async (e) => {
        e.preventDefault();
        try {
            const taskData = {
                ...taskForm,
                due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
                recurrence: taskForm.recurrence || 'none'
            };
            await createTask(taskData);
            toast.success('Aufgabe erstellt!');
            setDialogOpen(false);
            setTaskForm({
                title: '',
                description: '',
                category: 'general',
                priority: 'medium',
                due_date: '',
                recurrence: 'none',
                points: 10
            });
            loadTasks();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        }
    };

    const handleCompleteTask = async (id) => {
        try {
            const response = await completeTask(id);
            toast.success(`Aufgabe erledigt! +${response.data.points_earned} Punkte`);
            loadTasks();
        } catch (error) {
            toast.error('Fehler beim Abschließen');
        }
    };

    const handleDeleteTask = async (id) => {
        try {
            await deleteTask(id);
            toast.success(language === 'de' ? 'Aufgabe gelöscht' : 'Task deleted');
            loadTasks();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler beim Löschen' : 'Error deleting');
        }
    };

    const handleEditTask = (task) => {
        setEditingTask({
            ...task,
            due_date: task.due_date ? task.due_date.split('T')[0] : ''
        });
        setEditDialogOpen(true);
    };

    const handleUpdateTask = async () => {
        if (!editingTask) return;
        try {
            await updateTask(editingTask.id, {
                title: editingTask.title,
                description: editingTask.description,
                category: editingTask.category,
                priority: editingTask.priority,
                due_date: editingTask.due_date || null,
                recurrence: editingTask.recurrence || 'none',
                points: editingTask.points
            });
            toast.success(language === 'de' ? 'Aufgabe aktualisiert' : 'Task updated');
            setEditDialogOpen(false);
            setEditingTask(null);
            loadTasks();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Error saving');
        }
    };

    const getCategoryInfo = (cat) => categories.find(c => c.value === cat) || categories[0];
    const getPriorityInfo = (pri) => priorities.find(p => p.value === pri) || priorities[1];

    const pendingCount = tasks.filter(t => !t.completed).length;
    const completedCount = tasks.filter(t => t.completed).length;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="tasks-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center gap-3">
                            <ListTodo className="w-8 h-8 text-primary" />
                            Aufgaben
                        </h1>
                        <p className="text-muted-foreground mt-1">Organisiere deine Wellness-Ziele</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full gap-2" data-testid="add-task-btn">
                                <Plus className="w-4 h-4" />
                                Aufgabe
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle>Neue Aufgabe</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddTask} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Titel</Label>
                                    <Input
                                        id="title"
                                        value={taskForm.title}
                                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                                        placeholder="z.B. 2L Wasser trinken"
                                        required
                                        data-testid="task-title-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="description">Beschreibung (optional)</Label>
                                    <ClearableInput
                                        id="description"
                                        value={taskForm.description}
                                        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                                        placeholder="Weitere Details..."
                                        data-testid="task-description-input"
                                        clearTestId="task-description-clear"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Kategorie</Label>
                                        <Select
                                            value={taskForm.category}
                                            onValueChange={(v) => setTaskForm({ ...taskForm, category: v })}
                                        >
                                            <SelectTrigger data-testid="task-category-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((c) => (
                                                    <SelectItem key={c.value} value={c.value}>
                                                        {c.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Priorität</Label>
                                        <Select
                                            value={taskForm.priority}
                                            onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                                        >
                                            <SelectTrigger data-testid="task-priority-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {priorities.map((p) => (
                                                    <SelectItem key={p.value} value={p.value}>
                                                        {p.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="due_date">Fällig am</Label>
                                        <Input
                                            id="due_date"
                                            type="date"
                                            value={taskForm.due_date}
                                            onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                                            data-testid="task-due-date-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>{language === 'de' ? 'Wiederholung' : 'Recurrence'}</Label>
                                        <select
                                            value={taskForm.recurrence}
                                            onChange={(e) => setTaskForm({ ...taskForm, recurrence: e.target.value })}
                                            className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                                        >
                                            <option value="none">{language === 'de' ? 'Einmalig' : 'Once'}</option>
                                            <option value="daily">{language === 'de' ? 'Täglich' : 'Daily'}</option>
                                            <option value="weekly">{language === 'de' ? 'Wöchentlich' : 'Weekly'}</option>
                                            <option value="monthly">{language === 'de' ? 'Monatlich' : 'Monthly'}</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="points">Punkte</Label>
                                        <Input
                                            id="points"
                                            type="number"
                                            value={taskForm.points}
                                            onChange={(e) => setTaskForm({ ...taskForm, points: parseInt(e.target.value) || 10 })}
                                            min={1}
                                            max={100}
                                            data-testid="task-points-input"
                                        />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full" data-testid="submit-task-btn">
                                    Erstellen
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="cursor-pointer" onClick={() => setFilter('all')}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <ListTodo className="w-5 h-5 text-muted-foreground" />
                            <div>
                                <p className="text-2xl font-bold">{tasks.length}</p>
                                <p className="text-xs text-muted-foreground">Gesamt</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer" onClick={() => setFilter('pending')}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <Circle className="w-5 h-5 text-yellow-500" />
                            <div>
                                <p className="text-2xl font-bold">{pendingCount}</p>
                                <p className="text-xs text-muted-foreground">Offen</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer" onClick={() => setFilter('completed')}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                            <div>
                                <p className="text-2xl font-bold">{completedCount}</p>
                                <p className="text-xs text-muted-foreground">Erledigt</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <Star className="w-5 h-5 text-yellow-500" />
                            <div>
                                <p className="text-2xl font-bold">
                                    {tasks.filter(t => t.completed).reduce((sum, t) => sum + (t.points || 0), 0)}
                                </p>
                                <p className="text-xs text-muted-foreground">Punkte verdient</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Reminder Banner */}
                {(() => {
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    const urgentTasks = tasks.filter(t => !t.completed && t.due_date).map(t => {
                        const due = new Date(t.due_date);
                        due.setHours(0,0,0,0);
                        const diff = Math.ceil((due - today) / (1000*60*60*24));
                        return {...t, diffDays: diff};
                    }).filter(t => t.diffDays <= 1).sort((a,b) => a.diffDays - b.diffDays);
                    
                    if (urgentTasks.length === 0) return null;
                    return (
                        <Card className="border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20">
                            <CardContent className="p-3">
                                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    {language === 'de' ? 'Erinnerung' : 'Reminder'}
                                </p>
                                {urgentTasks.map(t => (
                                    <div key={t.id} className="flex items-center gap-2 text-sm py-1">
                                        <span className={t.diffDays < 0 ? 'text-red-600 font-bold' : 'text-amber-700'}>{t.diffDays < 0 ? '⚠️' : '⏰'}</span>
                                        <span className="flex-1 truncate">{t.title}</span>
                                        <span className="text-xs text-muted-foreground shrink-0">{t.diffDays < 0 ? `${Math.abs(t.diffDays)}T überfällig` : 'Heute!'}</span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    );
                })()}

                {/* Task List */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">
                                {filter === 'all' ? 'Alle Aufgaben' : filter === 'pending' ? 'Offene Aufgaben' : 'Erledigte Aufgaben'}
                            </CardTitle>
                            <Select value={filter} onValueChange={setFilter}>
                                <SelectTrigger className="w-32" data-testid="task-filter-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle</SelectItem>
                                    <SelectItem value="pending">Offen</SelectItem>
                                    <SelectItem value="completed">Erledigt</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {tasks.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Keine Aufgaben gefunden. Erstelle deine erste Aufgabe!
                            </p>
                        ) : (
                            tasks.map((task) => {
                                const catInfo = getCategoryInfo(task.category);
                                const priInfo = getPriorityInfo(task.priority);
                                return (
                                    <motion.div
                                        key={task.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex items-start gap-4 p-4 rounded-xl ${catInfo.color} ${task.completed ? 'opacity-60' : ''}`}
                                    >
                                        <Checkbox
                                            checked={task.completed}
                                            onCheckedChange={() => !task.completed && handleCompleteTask(task.id)}
                                            className="mt-1"
                                            data-testid={`task-checkbox-${task.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                                {task.title}
                                            </p>
                                            {task.description && (
                                                <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                                <Badge variant="outline" className="gap-1 text-xs">
                                                    <catInfo.icon className="w-3 h-3" />
                                                    {catInfo.label}
                                                </Badge>
                                                <Badge variant="outline" className={`gap-1 text-xs ${priInfo.color}`}>
                                                    <AlertCircle className="w-3 h-3" />
                                                    {priInfo.label}
                                                </Badge>
                                                {task.due_date && (() => {
                                                    const due = new Date(task.due_date);
                                                    const today = new Date();
                                                    today.setHours(0,0,0,0);
                                                    due.setHours(0,0,0,0);
                                                    const diffDays = Math.ceil((due - today) / (1000*60*60*24));
                                                    const isOverdue = diffDays < 0;
                                                    const isDueToday = diffDays === 0;
                                                    const isDueSoon = diffDays > 0 && diffDays <= 2;
                                                    // Avoid nested ternaries — explicit branches improve readability and debugging.
                                                    let badgeClass = '';
                                                    if (isOverdue) badgeClass = 'bg-red-100 text-red-700 border-red-300';
                                                    else if (isDueToday) badgeClass = 'bg-amber-100 text-amber-700 border-amber-300 animate-pulse';
                                                    else if (isDueSoon) badgeClass = 'bg-orange-100 text-orange-700 border-orange-300';

                                                    let badgeLabel = due.toLocaleDateString('de-DE');
                                                    if (isOverdue) badgeLabel = `Überfällig (${Math.abs(diffDays)}T)`;
                                                    else if (isDueToday) badgeLabel = 'Heute fällig!';
                                                    else if (isDueSoon) badgeLabel = `In ${diffDays}T`;

                                                    return (
                                                        <Badge variant="outline" className={`gap-1 text-xs ${badgeClass}`}>
                                                            <Clock className="w-3 h-3" />
                                                            {badgeLabel}
                                                        </Badge>
                                                    );
                                                })()}
                                                {task.recurrence && task.recurrence !== 'none' && (() => {
                                                    let recurrenceLabel = '↻ Monatlich';
                                                    if (task.recurrence === 'daily') recurrenceLabel = '↻ Täglich';
                                                    else if (task.recurrence === 'weekly') recurrenceLabel = '↻ Wöchentlich';
                                                    return (
                                                        <Badge variant="outline" className="gap-1 text-xs bg-primary/5">
                                                            {recurrenceLabel}
                                                        </Badge>
                                                    );
                                                })()}
                                                <Badge variant="secondary" className="gap-1 text-xs">
                                                    <Star className="w-3 h-3" />
                                                    {task.points} Pkt
                                                </Badge>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEditTask(task)}
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteTask(task.id)}
                                            className="text-destructive hover:text-destructive"
                                            data-testid={`delete-task-${task.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </motion.div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>
            </motion.div>

            {/* Edit Task Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="w-5 h-5" />
                            {language === 'de' ? 'Aufgabe bearbeiten' : 'Edit Task'}
                        </DialogTitle>
                    </DialogHeader>
                    {editingTask && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>{language === 'de' ? 'Titel' : 'Title'}</Label>
                                <Input
                                    value={editingTask.title}
                                    onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{language === 'de' ? 'Beschreibung' : 'Description'}</Label>
                                <Input
                                    value={editingTask.description || ''}
                                    onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{language === 'de' ? 'Kategorie' : 'Category'}</Label>
                                    <Select 
                                        value={editingTask.category} 
                                        onValueChange={(v) => setEditingTask({...editingTask, category: v})}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {categories.map((c) => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>{language === 'de' ? 'Priorität' : 'Priority'}</Label>
                                    <Select 
                                        value={editingTask.priority} 
                                        onValueChange={(v) => setEditingTask({...editingTask, priority: v})}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {priorities.map((p) => (
                                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{language === 'de' ? 'Fällig am' : 'Due Date'}</Label>
                                    <Input
                                        type="date"
                                        value={editingTask.due_date}
                                        onChange={(e) => setEditingTask({...editingTask, due_date: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>{language === 'de' ? 'Punkte' : 'Points'}</Label>
                                    <Input
                                        type="number"
                                        value={editingTask.points}
                                        onChange={(e) => setEditingTask({...editingTask, points: parseInt(e.target.value) || 10})}
                                        min={1}
                                        max={100}
                                    />
                                </div>
                            </div>
                            <Button onClick={handleUpdateTask} className="w-full gap-2">
                                <Check className="w-4 h-4" />
                                {language === 'de' ? 'Speichern' : 'Save'}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
